import React, { useState, useEffect, useCallback } from 'react';
import { 
  PhotoIcon, 
  FolderOpenIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Button from '../common/Button';
import MediaSelector from './MediaSelector';
import { formatFileSize } from '../../services/mediaService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// SortableItem component for individual draggable images
const SortableItem = ({ file, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id || `file-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group cursor-grab active:cursor-grabbing"
    >
      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
        <img
          src={file.url || file.preview}
          alt={file.name || `Upload ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Drag handle indicator */}
      <div className="absolute top-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        ⋮⋮
      </div>
      
      {/* Remove button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(file.id || index);
        }}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
      
      {/* File info */}
      <div className="mt-1 text-xs text-gray-500 truncate">
        <div className="truncate" title={file.name}>
          {file.name || 'Unknown'}
        </div>
        {file.size && (
          <div className="text-gray-400">
            {formatFileSize(file.size)}
          </div>
        )}
        {file.id && (
          <div className="text-blue-500">Library</div>
        )}
      </div>
    </div>
  );
};
const EnhancedMediaUploader = ({
  label = "Package Images",
  initialFiles = [],
  onFilesChange,
  minFiles = 6,
  maxFiles = 10,
  category = "packages",
  error = null
}) => {
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [currentFiles, setCurrentFiles] = useState(initialFiles);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update currentFiles when initialFiles prop changes
  useEffect(() => {
    console.log('🔄 EnhancedMediaUploader initialFiles changed:', initialFiles);
    setCurrentFiles(initialFiles);
  }, [initialFiles]);

  // Debug logging for currentFiles changes
  useEffect(() => {
    console.log('📋 EnhancedMediaUploader currentFiles updated:', currentFiles);
  }, [currentFiles]);

  const selectedMedia = currentFiles.filter(file => file.id && !file.file);

  const handleMediaSelection = useCallback((mediaFiles) => {
    console.log('📨 Received media selection:', mediaFiles);
    // Replace all current files with the selected media files
    setCurrentFiles(mediaFiles);
    if (onFilesChange) {
      console.log('📤 Calling onFilesChange with:', mediaFiles);
      onFilesChange(mediaFiles);
    }
  }, [onFilesChange]);

  const handleRemoveFile = useCallback((indexOrId) => {
    let updatedFiles;
    
    if (typeof indexOrId === 'string') {
      // Remove by ID (for media files)
      updatedFiles = currentFiles.filter(file => file.id !== indexOrId);
    } else {
      // Remove by index (for local files)
      updatedFiles = currentFiles.filter((_, index) => index !== indexOrId);
    }
    
    setCurrentFiles(updatedFiles);
    if (onFilesChange) {
      onFilesChange(updatedFiles);
    }
  }, [currentFiles, onFilesChange]);

  // Handle drag end event for reordering
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCurrentFiles((prevFiles) => {
        const oldIndex = prevFiles.findIndex((file, index) => (file.id || `file-${index}`) === active.id);
        const newIndex = prevFiles.findIndex((file, index) => (file.id || `file-${index}`) === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          console.error('Could not find indices for drag operation', { active: active.id, over: over.id, files: prevFiles });
          return prevFiles;
        }

        const reorderedFiles = arrayMove(prevFiles, oldIndex, newIndex);

        if (onFilesChange) {
          console.log('🔄 Files reordered via drag-and-drop:', reorderedFiles);
          onFilesChange(reorderedFiles);
        }

        return reorderedFiles;
      });
    }
  }, [onFilesChange]);

  const selectedMediaIds = selectedMedia.map(file => file.id);

  return (
    <div className="space-y-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {minFiles > 0 && (
            <span className="text-gray-500 ml-1">
              (Min: {minFiles}, Max: {maxFiles})
            </span>
          )}
        </label>
      )}

      {/* Media Selection Button */}
      <div className="flex space-x-3 mb-4">
        <Button
          type="button"
          onClick={() => setShowMediaSelector(true)}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <FolderOpenIcon className="h-4 w-4" />
          <span>Select & Upload Images (Max {maxFiles} images)</span>
        </Button>

        <div className="text-sm text-gray-600 flex items-center">
          <span>{currentFiles.length} / {maxFiles} images selected</span>
        </div>
      </div>

      {/* Current Files Grid */}
      {currentFiles.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={currentFiles.map((file, index) => file.id || `file-${index}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
              {currentFiles.map((file, index) => (
                <SortableItem
                  key={file.id || `file-${index}`}
                  file={file}
                  index={index}
                  onRemove={handleRemoveFile}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Empty state */}
      {currentFiles.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
          <div className="text-center">
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              No images selected
            </p>
            <p className="text-xs text-gray-500">
              Click "Select & Upload Images" to add images
            </p>
          </div>
        </div>
      )}

      {/* Validation messages */}
      {error && (
        <div className="text-red-600 text-sm mt-1">
          {error}
        </div>
      )}

      {currentFiles.length < minFiles && (
        <div className="text-amber-600 text-sm mt-1">
          Please select at least {minFiles} images
        </div>
      )}

      {/* Media Selector Modal */}
      {showMediaSelector && (
        <MediaSelector
          isOpen={showMediaSelector}
          onClose={() => setShowMediaSelector(false)}
          onSelectMedia={handleMediaSelection}
          category={category}
          maxSelection={maxFiles}
          selectedMediaIds={selectedMediaIds}
          title={`Select & Upload Images (Max ${maxFiles} images)`}
        />
      )}
    </div>
  );
};

export default EnhancedMediaUploader;
